import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import mamLogo from '@/assets/mamLogo.svg';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import {
  AUTH_STRINGS,
  CONSENT_POLICY_URL,
  PRIVACY_POLICY_URL,
} from '@/shared/constants';
import { type RegisterFormData, registerSchema } from '@/shared/schemas';
import { useRegister, useCountries, useStates } from '@/shared/hooks';

/**
 * Renders a policy link. When no URL is configured (env var unset), the label
 * is shown as plain, non-clickable text instead of an anchor.
 */
export function PolicyLink({
  href,
  label,
}: Readonly<{ href?: string; label: string }>) {
  const className = 'font-semibold text-brand-blue';
  if (!href) {
    return <span className={className}>{label}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${className} hover:underline`}
    >
      {label}
    </a>
  );
}

export default function Register() {
  const registerMutation = useRegister();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      email: '',
      country: '',
      state: '',
      consentAccepted: false,
    },
  });

  const selectedCountry = useWatch({ control, name: 'country' });
  const consentAccepted = useWatch({ control, name: 'consentAccepted' });

  const { data: countries = [], isLoading: isLoadingCountries } =
    useCountries();

  // Find the selected country's code to fetch states
  const selectedCountryCode = useMemo(() => {
    const country = countries.find((c) => c.name === selectedCountry);
    return country?.code ?? '';
  }, [countries, selectedCountry]);

  const { data: states = [], isLoading: isLoadingStates } =
    useStates(selectedCountryCode);

  const onSubmit = ({ consentAccepted, ...registerData }: RegisterFormData) => {
    // The checkbox gates submission; consent is recorded atomically by the
    // backend via `acceptConsent` in the signup payload.
    registerMutation.mutate({
      ...registerData,
      acceptConsent: consentAccepted,
    });
  };

  const handleCountryChange = (
    value: string,
    onChange: (v: string) => void
  ) => {
    onChange(value);
    // Reset state and clear its validation error when country changes
    setValue('state', '');
    clearErrors('state');
  };

  const header = (
    <div className="flex justify-center mb-8 sm:mb-12">
      <img
        src={mamLogo}
        alt="MamBaby Logo"
        className="w-12 h-12"
        width="48"
        height="48"
        fetchPriority="high"
      />
    </div>
  );

  // Success state
  if (registerMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
          {header}
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">
              {AUTH_STRINGS.REGISTER.SUCCESS_TITLE}
            </h1>
            <p className="text-muted-foreground">
              {AUTH_STRINGS.REGISTER.SUCCESS_MESSAGE}
            </p>
            <Link
              to="/login"
              className="inline-block mt-4 px-6 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-primary-hover"
            >
              {AUTH_STRINGS.REGISTER.GO_TO_LOGIN}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Centered White Card */}
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
        {header}

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            {AUTH_STRINGS.REGISTER.TITLE}
          </h1>
          <p className="text-muted-foreground text-base">
            {AUTH_STRINGS.REGISTER.SUBTITLE}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Error Alert */}
          {registerMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {registerMutation.error?.message ||
                  AUTH_STRINGS.REGISTER.ERROR_MESSAGE}
              </AlertDescription>
            </Alert>
          )}

          {/* Name Field */}
          <div className="space-y-2">
            <Label
              htmlFor="name"
              className="text-foreground text-sm font-medium"
            >
              {AUTH_STRINGS.REGISTER.NAME_LABEL}
            </Label>
            <Input
              id="name"
              type="text"
              placeholder={AUTH_STRINGS.REGISTER.NAME_PLACEHOLDER}
              {...register('name')}
              className="h-12 bg-input-muted border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              disabled={registerMutation.isPending}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-foreground text-sm font-medium"
            >
              {AUTH_STRINGS.REGISTER.EMAIL_LABEL}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={AUTH_STRINGS.REGISTER.EMAIL_PLACEHOLDER}
              {...register('email')}
              className="h-12 bg-input-muted border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              disabled={registerMutation.isPending}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Country and State Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Country Field */}
            <div className="space-y-2">
              <Label
                htmlFor="country"
                className="text-foreground text-sm font-medium"
              >
                {AUTH_STRINGS.REGISTER.COUNTRY_LABEL}
              </Label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) =>
                      handleCountryChange(value, field.onChange)
                    }
                    disabled={registerMutation.isPending || isLoadingCountries}
                  >
                    <SelectTrigger
                      id="country"
                      className="h-12 bg-input-muted border-0 text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <SelectValue
                        placeholder={
                          isLoadingCountries
                            ? 'Loading...'
                            : AUTH_STRINGS.REGISTER.COUNTRY_PLACEHOLDER
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.id} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.country && (
                <p className="text-sm text-destructive">
                  {errors.country.message}
                </p>
              )}
            </div>

            {/* State Field */}
            <div className="space-y-2">
              <Label
                htmlFor="state"
                className="text-foreground text-sm font-medium"
              >
                {AUTH_STRINGS.REGISTER.STATE_LABEL}
              </Label>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={
                      registerMutation.isPending ||
                      !selectedCountry ||
                      isLoadingStates
                    }
                  >
                    <SelectTrigger
                      id="state"
                      className="h-12 bg-input-muted border-0 text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <SelectValue
                        placeholder={
                          isLoadingStates
                            ? 'Loading...'
                            : AUTH_STRINGS.REGISTER.STATE_PLACEHOLDER
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.id} value={state.name}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.state && (
                <p className="text-sm text-destructive">
                  {errors.state.message}
                </p>
              )}
            </div>
          </div>

          {/* GDPR Consent */}
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Controller
                name="consentAccepted"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="consentAccepted"
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                    disabled={registerMutation.isPending}
                    aria-invalid={!!errors.consentAccepted}
                    className="mt-0.5"
                  />
                )}
              />
              <Label
                htmlFor="consentAccepted"
                className="text-sm font-normal text-foreground leading-snug"
              >
                {AUTH_STRINGS.REGISTER.CONSENT_LABEL_PREFIX}
                <PolicyLink
                  href={CONSENT_POLICY_URL}
                  label={AUTH_STRINGS.REGISTER.CONSENT_POLICY_LINK}
                />
                {AUTH_STRINGS.REGISTER.CONSENT_LABEL_SEPARATOR}
                <PolicyLink
                  href={PRIVACY_POLICY_URL}
                  label={AUTH_STRINGS.REGISTER.PRIVACY_POLICY_LINK}
                />
              </Label>
            </div>
            {errors.consentAccepted && (
              <p className="text-sm text-destructive">
                {errors.consentAccepted.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 text-white text-base font-medium rounded-lg mt-6 bg-brand-blue hover:bg-primary-hover"
            disabled={registerMutation.isPending || !consentAccepted}
          >
            {registerMutation.isPending ? (
              <span className="flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {AUTH_STRINGS.REGISTER.LOADING_TEXT}
              </span>
            ) : (
              AUTH_STRINGS.REGISTER.SUBMIT_BUTTON
            )}
          </Button>

          {/* Login Link */}
          <div className="text-center text-muted-foreground text-sm pt-4">
            {AUTH_STRINGS.REGISTER.HAVE_ACCOUNT}{' '}
            <Link
              to="/login"
              className="font-semibold hover:underline text-brand-blue"
            >
              {AUTH_STRINGS.REGISTER.LOGIN_LINK}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
